<script setup lang="ts">
import { getCategoryRoute, getTranslatedProperty } from "@shopware-pwa/helpers-next";
import type { Schemas } from "#shopware";
import { onKeyStroke, useActiveElement, onClickOutside } from "@vueuse/core";

type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const { navigationElements } = useNavigation();
const currentMenuPosition = ref<string | undefined>(undefined);
const activeElementId = ref<string | undefined>(undefined);

const route = useRoute();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

// Activation functions
const resetActiveClasses = (navigation: NavigationElement[]) => {
  if (!navigation) return;
  navigation.forEach((item) => {
    item.activeClass = false;
    if (item.children) {
      resetActiveClasses(item.children);
    }
  });
};

const setActiveClass = (
  navigation: NavigationElement[],
  elementId: string,
  shouldBeActive: boolean = true
): boolean => {
  for (const item of navigation) {
    if (item.id === elementId) {
      item.activeClass = shouldBeActive;
      return true;
    }
    if (item.children && setActiveClass(item.children, elementId, shouldBeActive)) {
      item.activeClass = shouldBeActive;
      return true;
    }
  }
  return false;
};

const findMatchingElementId = (
  navigation: NavigationElement[],
  routePath: string
): string | undefined => {
  for (const navigationElement of navigation) {
    const seoUrls = navigationElement.seoUrls as Schemas["SeoUrl"][] | undefined;

    if (seoUrls?.some((url) => url?.seoPathInfo === routePath.slice(1))) {
      return navigationElement.id;
    }

    if (navigationElement.children) {
      const childId = findMatchingElementId(navigationElement.children, routePath);
      if (childId) return childId;
    }
  }
  return undefined;
};

// Update active state without recursion
const updateActiveState = () => {
  if (!navigationElements.value) return;

  // First reset all
  resetActiveClasses(navigationElements.value);

  // Find matching element
  const matchingId = findMatchingElementId(navigationElements.value, route.path);

  if (matchingId) {
    activeElementId.value = matchingId;
    setActiveClass(navigationElements.value, matchingId);
  }
};

// Watch route changes only
watch(
  () => route.path,
  () => {
    nextTick(() => {
      updateActiveState();
      currentMenuPosition.value = undefined;
    });
  },
  { immediate: true }
);

// Handle click updates
const updateActiveClass = (navigationId: string, parentId: string | undefined) => {
  if (!navigationElements.value) return;

  activeElementId.value = navigationId;
  resetActiveClasses(navigationElements.value);
  setActiveClass(navigationElements.value, navigationId);
};

// Keyboard navigation for accessibility
const activeElement = useActiveElement();
onKeyStroke("ArrowDown", (e) => {
  if (
    activeElement.value?.parentElement?.nextElementSibling?.getElementsByTagName("a")[0]
  ) {
    e.preventDefault();
    activeElement.value.parentElement.nextElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

onKeyStroke("ArrowUp", (e) => {
  if (
    activeElement.value?.parentElement?.previousElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value?.parentElement.previousElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

// Menu size adjustment for responsiveness
const setMenuSize = () => {
  const menuContainer = document.querySelector(".main-menu .container");
  const spanBefore = document.querySelectorAll(".main-menu-bg .span-before");
  const spanAfter = document.querySelectorAll(".main-menu-bg .span-after");

  if (menuContainer && spanBefore && spanAfter) {
    const menuSize = (window.innerWidth - menuContainer.clientWidth) / 2 + 15;

    for (var i = 0; i < spanBefore.length; i++) {
      (spanBefore[i] as HTMLElement).style.width = menuSize + "px";
      (spanAfter[i] as HTMLElement).style.width = menuSize + "px";
    }
  }
};

// Lifecycle hooks
onMounted(() => {
  setMenuSize();
  window.addEventListener("resize", setMenuSize);
  updateActiveState();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", setMenuSize);
});
</script>

<template>
  <!-- Main Navigation Bar -->
  <nav class="border-b border-extra-light-grey bg-white hidden lg:block px-4">
    <div class="container mx-auto">
      <ul class="flex items-center justify-center relative">
        <li
          class="mx-5 py-4"
          v-for="(navigationElement, index) in navigationElements"
          :key="navigationElement.id"
          @mouseover="currentMenuPosition = navigationElement.id"
          @mouseleave="currentMenuPosition = undefined"
          @focusin="currentMenuPosition = navigationElement.id"
          :class="{
            relative:
              navigationElement.children?.length > 0 &&
              !navigationElement.children?.some(child => child.media?.thumbnails?.[0]?.url)
          }"
        >
          <NuxtLink
            role="menuitem"
            :target="
              navigationElement.externalLink || navigationElement.linkNewTab
                ? '_blank'
                : ''
            "
            :to="formatLink(getCategoryRoute(navigationElement))"
            class="relative transition duration-300 ease-in-out text-base font-bold text-dark-bg hover:text-red-bg rounded-md text-nowrap group"
            :class="{
              'text-dark-bg': (navigationElement as NavigationElement).activeClass,
            }"
            @click="updateActiveClass(navigationElement.id, navigationElement.parentId)"
          >
            {{ getTranslatedProperty(navigationElement, "name") }}
            <span
              v-if="(navigationElement as NavigationElement).activeClass"
              class="absolute left-0 -bottom-[4px] w-full h-[2px] bg-dark-bg group-hover:bg-red-bg transition-colors duration-300"
            ></span>
          </NuxtLink>

          <client-only>
            <!-- Grid Menu Layout -->
            <template
              v-if="navigationElement.children?.some(child => child.media?.thumbnails?.[0]?.url)"
            >
              <div
                class="absolute left-1/2 -translate-x-2/4 top-full w-[878px] p-8 bg-white shadow-lg rounded-lg group-hover:block z-20"
                :class="{
                  visible: currentMenuPosition === navigationElement.id,
                  hidden: currentMenuPosition !== navigationElement.id,
                }"
                @focusin="currentMenuPosition = navigationElement.id"
                @mouseleave="currentMenuPosition = undefined"
              >
                <div class="grid grid-cols-5 gap-6">
                  <div
                    v-for="child in navigationElement.children"
                    :key="child.id"
                    class="flex flex-col items-center"
                  >
                    <NuxtLink
                      :to="formatLink(getCategoryRoute(child))"
                      class="text-dark-bg font-bold hover:text-red-bg mt-2 text-center"
                      :class="{
                        'text-red-bg': (child as NavigationElement).activeClass,
                      }"
                      @click="updateActiveClass(child.id, child.parentId)"
                    >
                      <NuxtImg
                        v-if="child.media?.thumbnails[0]?.url"
                        :src="child.media?.thumbnails[0]?.url"
                        :alt="child.media?.translated.alt"
                        class="w-[134px] h-[90px] object-cover rounded-lg"
                        loading="lazy"
                        :format="child.media?.fileExtension"
                      />
                      <img
                        v-else
                        src="assets/images/placeholder-category-thumb.png"
                        alt="Placeholder image"
                        class="w-[134px] h-[90px] object-cover rounded-lg"
                      />
                      <span class="block mt-2">{{ child.translated.name }}</span>
                    </NuxtLink>
                  </div>
                </div>
                <div class="grid grid-cols-5 gap-6 mt-4"></div>
              </div>
            </template>

            <!-- Standard Dropdown Menu Layout -->
            <template v-else>
              <div
                class="z-15 absolute left-0 top-full w-48 bg-white text-dark-bg border-gray-300 shadow-lg"
                :class="{
                  visible: currentMenuPosition === navigationElement.id,
                  hidden: currentMenuPosition !== navigationElement.id,
                }"
                @focusin="currentMenuPosition = navigationElement.id"
                @mouseleave="currentMenuPosition = undefined"
                @onClickOutside="currentMenuPosition = undefined"
              >
                <NuxtLink
                  v-for="child in navigationElement.children"
                  :key="child.id"
                  :to="formatLink(getCategoryRoute(child))"
                  :class="{
                    'text-red-bg': (child as NavigationElement).activeClass,
                  }"
                  @click="updateActiveClass(child.id, child.parentId)"
                  class="block px-3 py-3 hover:text-dark-bg hover:bg-light-grey transition duration-300 ease-in-out"
                >
                  {{ child.translated.name }}
                </NuxtLink>
              </div>
            </template>
          </client-only>
        </li>
      </ul>
    </div>
  </nav>
</template>
